import {
  BehaviorSubject,
  catchError,
  delay,
  filter,
  from,
  mergeMap,
  Observable,
  of,
} from 'rxjs';
import { Dependencies } from '../../../../configurations/dependencies/dependencies.interface';
import { Action, AnyAction } from '../../../state/action.type';
import { AppState } from '../../../state/app.state';
import { signOut } from '../../../state/authentication/authentication.action';
import {
  updateUserProfile,
  updateUserProfileFailure,
  updateUserProfileSuccess,
} from '../../../state/user/user.action';
import {
  UpdateUserProfile,
  UPDATE_USER_PROFILE,
} from '../../../state/user/user.action.type';
import {
  TokenExpiredError,
  TokenInvalidError,
} from '../../errors/authentication.error';
import { AUTH_API_ERROR_MESSAGES } from '../../errors/gateway.errorMessages/authentication.error.messages';
import { USER_USECASE_ERROR_MESSAGES } from '../../errors/usecases.errorMessages/userUsecase.error.messages';
import { UserUnauthorizedError } from '../../errors/user.error';
import { EditableProfileFields, UserProfile } from '../../models/user.model';

const DEFAULT_RETRY_DELAY = 300;

export const updateUserProfileUsecase = (
  action$: Observable<Action>,
  _state$: BehaviorSubject<AppState>,
  dependencies: Dependencies,
): Observable<Action> =>
  action$.pipe(
    filter(
      (action: AnyAction): action is UpdateUserProfile =>
        action.type === UPDATE_USER_PROFILE,
    ),
    mergeMap((action: UpdateUserProfile) => {
      const { token, currentProfile, newData, retry } = action.payload;

      // Business logic for comparing fields
      const changedFields = Object.keys(newData).reduce((acc, key) => {
        const k = key as keyof EditableProfileFields;
        if (newData[k] !== currentProfile[k]) {
          acc[k] = newData[k];
        }
        return acc;
      }, {} as Partial<UserProfile>);

      // If no changes, return early with success and current profile
      if (Object.keys(changedFields).length === 0) {
        return of(updateUserProfileSuccess(currentProfile));
      }

      return from(
        dependencies.userGateway.updateUserProfile(
          action.payload.token,
          changedFields,
        ),
      ).pipe(
        mergeMap((userProfile: UserProfile) => {
          return [updateUserProfileSuccess(userProfile)];
        }),
        catchError((error: Error) => {
          console.warn('updateUserProfileUsecase failed:', error.message);
          const remainingRetries = retry ? retry - 1 : 0;
          if (remainingRetries) {
            return of(
              updateUserProfile({
                token,
                currentProfile,
                newData,
                retry: remainingRetries,
              }),
            ).pipe(delay(DEFAULT_RETRY_DELAY));
          }
          // Check for UserUnauthorizedError
          if (error instanceof UserUnauthorizedError) {
            return of(
              updateUserProfileFailure(
                USER_USECASE_ERROR_MESSAGES.UNAUTHORIZED,
              ),
              signOut(),
            );
          }

          // Check for TokenExpiredError or TokenInvalidError
          if (
            error instanceof TokenExpiredError ||
            error instanceof TokenInvalidError
          ) {
            return of(
              updateUserProfileFailure(AUTH_API_ERROR_MESSAGES.TOKEN_EXPIRED),
              signOut(),
            );
          }

          // General failure case
          return of(
            updateUserProfileFailure(USER_USECASE_ERROR_MESSAGES.UPDATE_FAILED),
          );
        }),
      );
    }),
  );
